import React from 'react';

import Stack from '@mui/material/Stack';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import StatusChip from './InvoiceStatusChip';
import { useTranslation } from 'react-i18next';


interface InvoicePaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  totalEntries?: number;
  pageSize?: number;
  pageSizeOptions?: number[];
  onPageSizeChange?: (size: number) => void;
  showingLabel?: string; // e.g. 'entries'
  statusBadges?: React.ReactNode; // Optional status badges (chips, etc)
  leftContent?: React.ReactNode; // Custom left content
  rightContent?: React.ReactNode; // Custom right content
  compact?: boolean; // Compact mode for mobile
}



// Helper: Render a group of status chips (for summary)
export interface InvoiceStatusChipGroupProps {
  summary: { value: string; count: number }[];
  selectedStatus?: { value: string; count: number } | null;
  onStatusSelect?: (status: { value: string; count: number }) => void;
}

export const InvoiceStatusChipGroup: React.FC<InvoiceStatusChipGroupProps> = ({ summary, selectedStatus, onStatusSelect }) => (
  <Box 
    component="section"
    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
    role="group"
    aria-label="Invoice status filters"
  >
    {summary.map((s) => (
      <StatusChip
        key={s.value}
        data-testid={`invoice-pagination-status-${s.value.toLowerCase()}`}
        status={s.value}
        count={s.count}
        selected={selectedStatus?.value === s.value}
        onClick={() => onStatusSelect && onStatusSelect(s)}
        role="button"
        tabIndex={0}
        aria-pressed={selectedStatus?.value === s.value}
      />
    ))}
  </Box>
);

const InvoicePagination: React.FC<InvoicePaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  totalEntries = 0,
  pageSize = 10,
  pageSizeOptions= [10, 15, 25, 50],
  onPageSizeChange,
  statusBadges,
  leftContent,
  rightContent,
  compact = false,
}) => {
  // Calculate range for display
  const start = totalEntries === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const end = Math.min(currentPage * pageSize, totalEntries);
  const { t } = useTranslation('general');

  // Example: you can pass statusBadges={<InvoiceStatusChipGroup summary={[...]} />} from parent

  return (
    <Stack
      component="nav"
      direction={{ xs: compact ? 'column' : 'column', sm: 'row' }}
      alignItems={{ xs: 'stretch', sm: 'center' }}
      justifyContent="space-between"
      spacing={compact ? 1 : 2}
      sx={{ width: '100%', mt: compact ? 2 : 3, mb: compact ? 0.5 : 1, px: compact ? 1 : 0 }}
      role="navigation"
      aria-label="Invoice list pagination and filters"
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', minWidth: 0 }}>
        {leftContent}
        <Typography 
          variant="body2" 
          color="text.secondary" 
          sx={{ whiteSpace: 'nowrap', minWidth: 120 }}
          id="pagination-status"
        >
          {t('pagination.showingEntries', { start, end, totalCount: totalEntries })}
        </Typography>
        <Box sx={{ borderLeft: '1px solid #e0e0e0', height: 20, mx: 2, display: { xs: 'none', sm: 'block' } }} aria-hidden="true" />
        {statusBadges}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {rightContent}
        {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && (
          <TextField
            data-testid="invoice-pagination-page-size"
            select
            size={compact ? 'small' : 'small'}
            label={t('rows')}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            sx={{ width: 92 }}
            aria-label="Number of rows per page"
            aria-describedby="pagination-status"
          >
            {pageSizeOptions.map((opt) => (
              <MenuItem 
                key={opt} 
                value={opt} 
                data-testid={`invoice-pagination-page-size-option-${opt}`}
              >
                {opt}
              </MenuItem>
            ))}
          </TextField>
        )}
        <Pagination
          data-testid="invoice-pagination-control"
          count={totalPages}
          page={currentPage}
          onChange={(_, page) => onPageChange(page)}
          shape="rounded"
          color="primary"
          siblingCount={0}
          boundaryCount={1}
          showFirstButton={false}
          showLastButton={false}
          size={compact ? 'small' : 'medium'}
          aria-label="Invoice list page navigation"
          aria-current="page"
          sx={{
            '& .MuiPaginationItem-root': {
              minWidth: compact ? 28 : 32,
              height: compact ? 28 : 32,
              fontWeight: 600,
              fontSize: compact ? 14 : 16,
              borderRadius: 2,
            },
          }}
        />
      </Box>
    </Stack>
  );
};

export default InvoicePagination;
